import { StrictMode, useState, useCallback } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/auth/context'
import { SplashOverlay } from '@/components/SplashOverlay'
import { Capacitor } from '@capacitor/core'
import { router } from './routes'
import './index.css'

// Initialize sounds for both native and web
// (native splash hiding is now handled by SplashOverlay component)
import { initSounds } from '@/lib/native/sounds'
initSounds()

// Font options to match SettingsPage exactly
const FONT_CLASSES: Record<string, string> = {
  inter: 'font-inter',
  space: 'font-space',
  jetbrains: 'font-jetbrains',
  vt323: 'font-vt323',
}

// Update the theme-color meta tag and notify native iOS about theme changes
function updateThemeColorMeta(isDark: boolean) {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', isDark ? '#020617' : '#f8fafc')
  }
  // Notify native iOS to update overscroll/bounce background colors
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).webkit?.messageHandlers?.themeChange?.postMessage({ isDark })
  } catch {
    // Not on iOS or message handler not available yet
  }
}

// Initialize theme and font before React renders to prevent flash
function initializeTheme() {
  const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null
  const theme = storedTheme || 'system'
  
  // Force remove and re-add to ensure proper state
  document.documentElement.classList.remove('dark')
  
  let isDark = false
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
    isDark = true
  } else if (theme === 'system') {
    // System preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.classList.add('dark')
      isDark = true
    }
  }
  // Light mode: dark class already removed
  
  // Update meta tag and native background for initial theme
  updateThemeColorMeta(isDark)
  
  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const currentTheme = localStorage.getItem('theme')
    if (currentTheme === 'system' || !currentTheme) {
      if (e.matches) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
      updateThemeColorMeta(e.matches)
    }
  })
}

function initializeFont() {
  const storedFont = localStorage.getItem('app-font') || 'inter'
  const fontClass = FONT_CLASSES[storedFont] || 'font-inter'
  
  // Remove all font classes first (including any that might have been applied)
  document.body.classList.remove('font-inter', 'font-space', 'font-jetbrains', 'font-vt323', 'font-sans')
  
  // Apply stored font class
  document.body.classList.add(fontClass)
}

// Also apply font to documentElement for immediate effect before body exists
function initializeFontEarly() {
  const storedFont = localStorage.getItem('app-font') || 'inter'
  const fontClass = FONT_CLASSES[storedFont] || 'font-inter'
  
  // Apply to html element immediately (body might not exist yet)
  document.documentElement.classList.remove('font-inter', 'font-space', 'font-jetbrains', 'font-vt323', 'font-sans')
  document.documentElement.classList.add(fontClass)
}

// Initialize theme and font immediately
initializeTheme()
initializeFontEarly() // Apply to html element immediately

// Font also needs to be applied to body after it exists
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFont)
} else {
  initializeFont()
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Root component that manages the animated splash overlay
function App() {
  const isNative = Capacitor.isNativePlatform()
  const [splashComplete, setSplashComplete] = useState(!isNative)
  const handleSplashComplete = useCallback(() => setSplashComplete(true), [])

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
      {!splashComplete && (
        <SplashOverlay onComplete={handleSplashComplete} />
      )}
    </>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
